import {Component, computed, input, signal} from '@angular/core';
import {CircleXIcon, LucideAngularModule, SearchIcon} from 'lucide-angular';
import {FieldContainerComponent} from '@shared/form/field-container/field-container.component';
import {Choice} from '@shared/form/choice/choice';
import {FieldBaseComponent} from '@shared/form/field-base.component';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs';

@Component({
  selector: 'app-autocomplete-field',
  imports: [
    LucideAngularModule,
    FieldContainerComponent,
    ReactiveFormsModule
  ],
  templateUrl: './autocomplete-field.component.html',
  styleUrl: './autocomplete-field.component.scss'
})
export class AutocompleteFieldComponent<valueT> extends FieldBaseComponent<valueT | null> {

  public static readonly MIN_CHARS = 3;
  public static readonly DEBOUNCE_MILLIS = 250;

  public readonly choiceProvider = input.required<(input: string) => Observable<Choice<valueT>[]>>();
  public readonly valueLabel = input.required<(value: valueT) => string | null>();
  public readonly createLabel = input<(input: string) => string>((str: string) => `Créer "${str}"`);
  public readonly createChoice = input<((input: string) => Observable<Choice<valueT> | null>) | null>(null);
  public readonly createChoiceLabel = signal<string | null>(null);

  public readonly icons = {
    search: SearchIcon,
    close: CircleXIcon
  };

  public readonly searchControl = new FormControl<string | null>(null);
  public readonly searchText$ = new Subject<string>();
  public readonly showSpinner = signal(false);
  public readonly choices = signal<Choice<valueT>[] | null>(null);
  public readonly showList = computed(() => this.choices() || this.showSpinner());
  public readonly selectedLabel = signal<string | null>(null);

  public override ngOnInit(): void {
    super.ngOnInit();
    this.searchText$.pipe(
      takeUntil(this.destroy$),
      debounceTime(AutocompleteFieldComponent.DEBOUNCE_MILLIS),
      distinctUntilChanged(),
      switchMap(
        searchText => {
          const choiceProvider = this.choiceProvider();
          const choices$ = searchText && searchText.length >= AutocompleteFieldComponent.MIN_CHARS
            ? choiceProvider(searchText)
            : of([]);
          this.showSpinner.set(true);
          return choices$.pipe(
            tap(suggestions => {
              this.choices.set(suggestions ?? null);
            }),
            finalize(() => {
              this.showSpinner.set(false);
              const showCreate = !!this.createChoice() && searchText.length >= AutocompleteFieldComponent.MIN_CHARS;
              this.createChoiceLabel.set(showCreate ? this.createLabel()(searchText) : null);
            })
          );
        }
      )
    ).subscribe(_ => {});

    this.control.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(newVal => {
      this.selectedLabel.set(newVal ? this.valueLabel()(newVal) : null);
    });
  }

  public search(_event: KeyboardEvent): void {
    this.searchText$.next(this.searchControl.value ?? '');
  }

  public triggerCreate(input: HTMLInputElement): void {
    const choiceCreator = this.createChoice();
    if (!choiceCreator) {
      return;
    }
    choiceCreator(this.searchControl.value ?? '').pipe(
      take(1)
    ).subscribe(newChoice => {
      if (newChoice === null) {
        this.select(null, input);
      } else {
        const currentChoices = this.choices() ?? [];
        const newChoices = [newChoice, ...currentChoices];
        this.choices.set(newChoices);
        this.select(newChoice, input);
      }
    });
  }

  public select(choice: Choice<valueT> | null, searchInput: HTMLInputElement | null = null): void {
    this.setValue(choice?.value ?? null);
    this.searchControl.patchValue(null, {emitEvent: false});
    if (choice?.value) {
      this.searchControl.disable({emitEvent: false});
    } else {
      this.searchControl.enable({emitEvent: false});
    }
    this.choices.set(null);
    if (searchInput) {
      setTimeout(() => {
        if (choice?.value) {
          searchInput.blur();
        } else {
          searchInput.focus();
        }
      });
    }
  }

  public clear(input: HTMLInputElement): void {
    this.select(null, input);
  }

}
